import { Col, DatePicker, Form, Modal, Row, message } from 'antd';
import { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Document, Page } from 'react-pdf';
import { DocumentCallback } from 'react-pdf/dist/cjs/shared/types';
import receivablePublicAnnouncementBuildApi from '../../../../apis/ReceivablePublicAnnouncementBuildApi';
import receivablePublicAnnouncementSentApi from '../../../../apis/ReceivablePublicAnnouncementSentApi';
import { Receivable, ReceivablePublicAnnouncementBuild, ReceivablePublicAnnouncementSent } from '../../../../models/Entities';
import alertService from '../../../../services/AlertService';
import styles from './ReceivablePublicAnnouncementSentModal.module.scss';

const ReceivablePublicAnnouncementSentModal = (props: Props) => {
    const { receivable, publicAnnouncementBuild } = props;

    /*** HOOKS ***/

    const intl = useIntl();
    const [form] = Form.useForm();
    const [loading, setLoading] = useState<'loading' | 'saving'>();
    const [document, setDocument] = useState<Blob>();
    const [pages, setPages] = useState<number>(0);

    /*** EFFECTS ***/

    useEffect(() => {
        const init = async () => {
            try {
                setLoading('loading');
                const document: Blob = await receivablePublicAnnouncementBuildApi.download(publicAnnouncementBuild.id!);
                setDocument(document);
            } catch (error) {
                alertService.displayError(error, intl);
            } finally {
                setLoading(undefined);
            }
        };
        init();
    }, [intl, receivable, publicAnnouncementBuild.id, form]);

    /*** METHODS ***/

    //save
    const save = async () => {
        try {
            setLoading('saving');
            const values = await form.validateFields();
            let receivablePublicAnnouncementSent: ReceivablePublicAnnouncementSent = Object.assign({}, { receivableId: receivable!.id }, values);
            await receivablePublicAnnouncementSentApi.create(receivablePublicAnnouncementSent);
            await props.onSave();
            message.success(intl.formatMessage({ id: 'status.saved' }));
        } catch (error: any) {
            !error.errorFields && alertService.displayError(error, intl);
        } finally {
            setLoading(undefined);
        }
    };

    // cancel
    const cancel = async () => {
        props.onCancel();
    };
    // init document
    const initDocument = (document: DocumentCallback) => {
        setPages(document.numPages);
    };

    /*** VISUAL ***/

    return (
        <Modal
            title={<FormattedMessage id="receivable.publicAnnouncementSent.modal.title" />}
            open={true}
            onCancel={cancel}
            onOk={save}
            okText={<FormattedMessage id="button.save" tagName="span" />}
            okButtonProps={{ loading: loading === 'saving', size: 'large' }}
            cancelButtonProps={{ disabled: loading === 'saving', size: 'large' }}
            width={1000}
        >
            <Form form={form} colon={false} layout="vertical">
                <Row gutter={[28, 0]}>
                    <Col span={24}>
                        <Form.Item
                            label={<FormattedMessage id="receivable.notificationSent.modal.date" />}
                            name="sent"
                            rules={[{ required: true, message: <FormattedMessage id="status.mandatory" /> }]}
                        >
                            <DatePicker />
                        </Form.Item>
                    </Col>
                </Row>
                <Row gutter={[28, 0]}>
                    <Col span={24}>
                        <div className={styles.document}>
                            {document && (
                                <Document file={document} onLoadSuccess={initDocument}>
                                    {Array.from(new Array(pages), (el, index) => (
                                        <Page key={`page${index + 1}`} pageNumber={index + 1} width={935} />
                                    ))}
                                </Document>
                            )}
                        </div>
                    </Col>
                </Row>
            </Form>
        </Modal>
    );
};
export default ReceivablePublicAnnouncementSentModal;

interface Props {
    receivable: Receivable | undefined;
    publicAnnouncementBuild: ReceivablePublicAnnouncementBuild;
    onSave: () => Promise<void>;
    onCancel: () => void;
}
